import { useI18n } from "i18n";
import { selectCurrentEventCode } from "modules/event/selectors";
import { useClearFloorDataAndRegisterListeners } from "modules/joinFloor/hooks/useClearFloorDataAndRegisterListeners";
import { addErrorNotification } from "modules/notification/redux/notificationSlice";
import { setVisible as setAssignedTableNudgeVisible } from "modules/assignTables/redux";
import {
  changeSpace,
  changeSpaceCompleted,
} from "modules/space/redux/spaceSlice";
import { useMutation } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import { useAppDispatch } from "store/hooks";
import { useUserSession } from "modules/eventUserPresence/hooks/userSession";
import { useJoinMeeting } from "modules/audioVideo/hooks/useJoinMeeting/useJoinMeeting";
import { getErrorMessage } from "./errorHandler";
import { JoinEventPayload, request } from "./request";

export const useJoinEvent = () => {
  const { t } = useI18n(["server", "eventLanding", "common"]);
  const dispatch = useAppDispatch();
  const { clearFloorDataAndRegisterListeners } =
    useClearFloorDataAndRegisterListeners();
  const history = useHistory();
  const eventCode = useSelector(selectCurrentEventCode);
  const { createSession } = useUserSession();
  const { joinMeeting } = useJoinMeeting();

  const { mutate, status } = useMutation({
    mutationFn: async ({ eventId, floorId, tableId }: JoinEventPayload) => {
      dispatch(changeSpace());
      const {
        floorId: floorToJoin,
        tableId: tableToJoin,
        isAssigned,
      } = await request({
        eventId,
        tableId,
        floorId,
      });
      await Promise.all([
        createSession({ eventId, floorId: floorToJoin }),
        joinMeeting({
          eventId,
          conversationId: tableToJoin,
        }),
      ]);

      return {
        floorId: floorToJoin,
        isAssigned,
      };
    },
    onSuccess: ({ floorId, isAssigned }) => {
      clearFloorDataAndRegisterListeners(floorId);

      dispatch(setAssignedTableNudgeVisible(isAssigned));
    },
    onError: (error: Error) => {
      const { notification, action } = getErrorMessage(error.message);

      if (notification) {
        dispatch(
          addErrorNotification({
            message: t(notification),
          }),
        );
      }

      const url = [`${eventCode}/register`];

      if (action) {
        url.push(`action=${action}`);
      }

      history.push(url.join("?"));
    },
    onSettled: () => {
      dispatch(changeSpaceCompleted());
    },
  });

  return { joinEvent: mutate, status };
};
